import React, { useState, useEffect } from 'react';
import {
  Button,
  FormControl,
  Grid,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Theme,
  Link,
  Box,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Search } from '@material-ui/icons';
import { useForm, FormProvider } from 'react-hook-form';
import { TextFieldController } from '../../../../components/organisms/form/controllers/TextFieldController';
import { SelectController } from '../../../../components/organisms/form/controllers/SelectController';
import { BasicTableHead, BasicTableHeadProps } from '../../../../components/molecules';
import { useHistory } from 'react-router-dom';
import { ListCouponPackIssues, useCouponPacks } from '../hooks/useCouponPacks';
import { ReadMoreButton } from '../../../../components/organisms/general/ReadMoreButton';

const useStyles = makeStyles((theme: Theme) => ({
  conditionFormControl: {
    minWidth: 150,
    maxWidth: 300,
  },
  searchForm: {
    marginTop: theme.spacing(1),
  },
  searchButton: {
    textAlign: 'center',
  },
  table: {
    '& th': {
      backgroundColor: theme.palette.grey[200],
      fontWeight: 'bold',
    },
  },
  urlCell: {
    maxWidth: 300,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  detailButton: {
    minWidth: 80,
  },
  resultList: {
    marginTop: theme.spacing(3),
  },
  btnSearch: {
    marginTop: theme.spacing(2),
  },
}));

export type SearchFormValues = {
  value: string;
  searchCondition: string;
};

export const CouponPackMasterIssuedListTabBody: React.FC<ListCouponPackIssues> = (props) => {
  const classes = useStyles();
  const history = useHistory();
  const theme = useTheme();

  const searchForm = useForm<SearchFormValues>({
    defaultValues: {
      value: '',
      searchCondition: 'none',
    },
  });

  const { handleSubmit, watch, setValue } = searchForm;
  const watchSearchCondition = watch('searchCondition', 'none');

  useEffect(() => {
    setValue('value', '');
  }, [watchSearchCondition, setValue]);

  const tableHeadProps: BasicTableHeadProps = {
    columns: [
      {
        name: 'couponPackId',
        label: '回数券ID',
        minWidth: theme.spacing(40),
        align: 'left',
      },
      {
        name: 'couponPackUrl',
        label: '回数券URL',
        minWidth: theme.spacing(50),
        align: 'left',
      },
      {
        name: 'action',
        label: '',
        minWidth: theme.spacing(10),
        align: 'left',
      },
    ],
  };

  const onSearch = (data: SearchFormValues): void => {
    props.onSearchCouponPacks(data);
  };

  const renderSearchForm = (): React.ReactElement => {
    switch (watchSearchCondition) {
      case 'serialNo':
        return (
          <Box width={'300px'}>
            <TextFieldController
              name="value"
              label="回数券IDが"
              variant="standard"
              rule={{
                required: {
                  value: true,
                  message: '条件を指定する場合、必須です',
                },
                maxLength: {
                  value: 16,
                  message: '16文字以内で入力してください',
                },
              }}
            />
            <Grid item xs={12} className={classes.btnSearch}>
              <Button
                type="submit"
                variant="outlined"
                size="large"
                color={'default'}
                disabled={props.loading}
                endIcon={<Search />}
                fullWidth
              >
                検索
              </Button>
            </Grid>
          </Box>
        );
      case 'couponPackUrl':
        return (
          <Box width={'300px'}>
            <TextFieldController
              name="value"
              label="回数券URLが"
              variant="standard"
              rule={{
                required: {
                  value: true,
                  message: '条件を指定する場合、必須です',
                },
                maxLength: {
                  value: 200,
                  message: '200文字以内で入力してください',
                },
              }}
            />
            <Grid item xs={12} className={classes.btnSearch}>
              <Button
                type="submit"
                variant="outlined"
                size="large"
                color={'default'}
                disabled={props.loading}
                endIcon={<Search />}
                fullWidth
              >
                検索
              </Button>
            </Grid>
          </Box>
        );
      default:
        return (
          <Box width={'300px'} className={classes.btnSearch}>
            <Button
              type="submit"
              variant="outlined"
              size="large"
              color={'default'}
              disabled={props.loading}
              endIcon={<Search />}
              fullWidth
            >
              検索
            </Button>
          </Box>
        );
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <FormProvider {...searchForm}>
          <form onSubmit={handleSubmit(onSearch)} autoComplete="off" noValidate>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl className={classes.conditionFormControl}>
                  <SelectController
                    name="searchCondition"
                    label="検索条件"
                    variant="standard"
                    menuItems={[
                      { value: 'none', label: 'なし' },
                      { value: 'serialNo', label: '回数券ID' },
                      { value: 'couponPackUrl', label: '回数券URL' },
                    ]}
                  />
                </FormControl>
              </Grid>
            </Grid>
            <Grid item>{renderSearchForm()}</Grid>
          </form>
        </FormProvider>
      </Grid>

      <Grid item xs={12} className={classes.resultList}>
        <TableContainer component={Paper}>
          {props.loading ? <LinearProgress /> : <React.Fragment />}
          <Table>
            <BasicTableHead columns={tableHeadProps.columns} />
            <TableBody>
              {(() => {
                if (!props.data || !props.data.couponPacks) {
                  return;
                } else if (!props.data?.couponPacks.nodes || props.data.couponPacks.nodes.length === 0) {
                  return (
                    <TableRow>
                      <TableCell>データがありません</TableCell>
                    </TableRow>
                  );
                } else {
                  return props.data.couponPacks.nodes.map((record, index) => (
                    <TableRow key={index}>
                      <TableCell component="th" scope="row">
                        {record.serialNo}
                      </TableCell>
                      <TableCell component="th" scope="row" className={classes.urlCell}>
                        {record.couponPackUrl ? (
                          <Link href={record.couponPackUrl} target="_blank" rel="noopener noreferrer">
                            {record.couponPackUrl}
                          </Link>
                        ) : null}
                      </TableCell>
                      <TableCell align="center" scope="row">
                        <Button
                          onClick={() =>
                            history.push({
                              pathname: `/coupon-pack-masters/${record.couponPackMasterId}/coupon-pack/${record.id}`,
                              search: history.location.search,
                            })
                          }
                          type="button"
                          size="small"
                          color="primary"
                        >
                          詳細
                        </Button>
                      </TableCell>
                    </TableRow>
                  ));
                }
              })()}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
      {props.data?.couponPacks.pageInfo.nextToken ? (
        <ReadMoreButton fetchMore={props.onFetchMore} loading={props.loading} />
      ) : (
        <></>
      )}
    </Grid>
  );
};

export const CouponPackMasterIssuedListTab: React.FC<{ couponPackMasterId: string }> = ({ couponPackMasterId }) => {
  const couponPacks = useCouponPacks(couponPackMasterId);
  return <CouponPackMasterIssuedListTabBody {...couponPacks} />;
};
